import { defineStore } from "pinia";
import { ListManager } from "../supplement-editor/utils/list-manager";
import { Transformers } from "../supplement-editor/utils/transformers";
import { Factories } from "../supplement-editor/utils/factories";
import { SupplementApi } from "./supplement-api";
import { debugLog } from "../supplement-editor/utils/logger";
import { collectUsedFootnoteSymbols } from "../supplement-editor/utils/footnote-symbols";

export const useSupplementLabelStore = defineStore("supplementLabel", {
  state: () => ({
    id: null,
    userId: null,
    lockVersion: 0,
    serverLockVersion: 0,
    isConflicted: false,
    name: "",
    servingSizeText: "",
    servingsPerContainer: "",
    amountPerText: "Serving",
    singleRowLayout: false,
    ageCategory: "adult",
    displayAgeCategories: [],
    format: "fda/supplement_adult/vertical",

    _uidCounter: 0,

    dvLines: [],
    linesWithoutDv: [],
    dietaryLines: [],
    blends: [],
    otherIngredients: "",
    allergens: "",
    facilityAllergens: "",
    businessInfo: "",
    boldOptions: {},

    // Defaults from user's default recipe label (for autofill feature)
    defaultAllergens: "",
    defaultFacilityAllergens: "",
    defaultFacilityAllergensPrefix: "",

    hideName: false,
    hideFacts: false,
    hideOtherIngredients: false,
    hideAllergens: false,
    hideFacilityAllergens: false,
    hideBusinessInfo: false,
    hideLogo: false,
    hideBarcode: false,
    hideServingsPerContainer: false,

    textColor: "#000000",
    backgroundColor: "#ffffff",
    transparentBackground: false,
    textAlign: "left",
    textTransform: "none",
    useLargerFootnotes: false,
    labelWidth: 227,

    // Synced from CSS in initialize to avoid DOM access in getters
    dvColumnWidth: 65,

    logoImage: null,
    logoScale: 1.0,
    logoMargin: 8,
    primaryBarcode: null,

    isPublic: false,
    watermark: false,
    bioengineered: false,
    proteinScore: null,

    lastImportedRecipe: null,

    dietaryNutrientOptions: [],
    fdaNutrientOptions: [],
    formatOptions: [],
    ageCategoryOptions: [],
    blendDisplayModeOptions: [],
    amountPerTextOptions: [],
    subNutrientParents: {},
    validationWarnings: {},

    calculatedValues: {
      dietary_lines: [],
      dv_lines: [],
      blends: [],
      servings_per_container_text: "",
    },

    // FDA rounding rules from server (single source of truth)
    roundingRules: {},

    isCalculating: false,
    isSaving: false,
    lastSavedAt: null,
    error: null,
    justAddedDvLineIndex: null,
    justAddedLineIndex: null,
    justAddedBlendIndex: null,

    lastFailedPayload: null,
    canRetry: false,
    showSpinner: false,
    retryCallback: null,

    renderedDimensionsText: "",

    isReloading: false,
    lastReloadAt: null,
  }),

  getters: {
    hasUnsavedChanges: (state) => {
      return state.lastSavedAt === null;
    },

    isOutOfSync: (state) => {
      return state.serverLockVersion > state.lockVersion;
    },

    effectiveDisplayAgeCategories: (state) => {
      const cats = state.displayAgeCategories || [];
      return cats.length > 0 ? [state.ageCategory, ...cats] : [state.ageCategory];
    },

    isMultiColumnDv(state) {
      return this.effectiveDisplayAgeCategories.length > 1;
    },

    additionalWidthForColumns(state) {
      // SAFE: Uses state variable synced in initialize() instead of accessing DOM
      return (this.effectiveDisplayAgeCategories.length || 0) * state.dvColumnWidth;
    },

    effectiveLabelWidth(state) {
      const baseWidth = state.labelWidth || 227;
      return baseWidth + this.additionalWidthForColumns;
    },

    // OPTIMIZED: No side-effects/mutation of state
    dietaryNutrientsMap(state) {
      const map = {};
      state.dietaryNutrientOptions.forEach((opt) => {
        map[opt.value] = {
          label: opt.label,
          unit: opt.unit,
          indent_level: opt.indent_level,
          order: opt.order,
        };
      });
      return map;
    },

    fdaNutrientsMap(state) {
      const map = {};
      state.fdaNutrientOptions.forEach((opt) => {
        map[opt.value] = {
          name: opt.label,
          unit: opt.unit,
          group: opt.group,
          dv: opt.dv,
        };
      });
      return map;
    },

    // NEW: Cached sort order for performance
    nutrientSortOrder(state) {
      const map = new Map();
      state.fdaNutrientOptions.forEach((opt, index) => {
        map.set(opt.value, index);
      });
      return map;
    },

    layoutSignature(state) {
      // PRESERVED: String format for compatibility
      return [
        state.hideLogo,
        state.logoScale,
        state.logoMargin,
        state.hideBarcode,
        state.hideName,
        state.hideFacts,
        state.hideAllergens,
        state.hideFacilityAllergens,
        state.hideBusinessInfo,
        this.effectiveLabelWidth,
        state.singleRowLayout,
        state.hideServingsPerContainer,
        state.ageCategory,
        state.displayAgeCategories.join(","),
        state.textColor,
        state.backgroundColor,
        state.textAlign,
        state.textTransform,
        state.useLargerFootnotes,
      ].join("|");
    },

    hideDaggerLabel() {
      return "Hide dagger (†)";
    },

    dietaryWarnings(state) {
      return state.validationWarnings.dietary || [];
    },

    dvWarnings(state) {
      return state.validationWarnings.dv || [];
    },

    lineWarnings(state) {
      return state.validationWarnings.lines || [];
    },

    usedFootnoteSymbols(state) {
      return collectUsedFootnoteSymbols(state);
    },
  },

  actions: {
    _generateUid() {
      return ++this._uidCounter;
    },

    debugLog(...args) {
      debugLog(...args);
    },

    initialize(data) {
      // SYNC CSS VARS SAFELY: Read once on init
      if (typeof window !== "undefined") {
        const cssVar = getComputedStyle(document.documentElement)
          .getPropertyValue("--sf-dv-col-width")
          .trim();
        this.dvColumnWidth = parseInt(cssVar, 10) || 65;
      }

      const state = Transformers.labelFromServer(data, this._generateUid.bind(this));
      this.$patch(state);
    },

    // --- Internal Helpers (Preserves logic, reduces repetition) ---
    _addToList(list, item, trackerKey = null) {
      const idx = ListManager.add(list, item, () => this._generateUid());
      if (trackerKey) this[trackerKey] = idx;
      return idx;
    },

    // --- DV Lines ---
    addDvLine(dvKey = "") {
      if (dvKey && this.hasDuplicateDvKey(dvKey)) {
        window.Flash?.error(`${dvKey} is already added`);
        return false;
      }

      const newItem = Factories.createDvLine(dvKey);
      ListManager.add(this.dvLines, newItem, () => this._generateUid());

      // Special handling: Sort and find new index
      const addedUid = this.dvLines[this.dvLines.length - 1]._uid;
      this.sortDvLines();
      this.justAddedDvLineIndex = this.dvLines.findIndex((item) => item._uid === addedUid);
      return true;
    },

    sortDvLines() {
      const orderMap = this.nutrientSortOrder; // Use cached getter
      this.dvLines.sort((a, b) => {
        const indexA = orderMap.has(a.dv_key) ? orderMap.get(a.dv_key) : Infinity;
        const indexB = orderMap.has(b.dv_key) ? orderMap.get(b.dv_key) : Infinity;
        return indexA - indexB;
      });
    },

    updateDvLineKey(uid, dvKey, nutrientData) {
      const currentLineIndex = this.dvLines.findIndex((l) => l._uid === uid);
      if (this.hasDuplicateDvKey(dvKey, currentLineIndex)) {
        window.Flash?.error(`${dvKey} is already added`);
        return false;
      }

      const line = this.dvLines[currentLineIndex];
      if (line) {
        line.dv_key = dvKey || "";
        line.name = nutrientData?.name || "";
        line.unit = nutrientData?.unit || "";
        this.sortDvLines();
      }
      return true;
    },

    hasDuplicateDvKey(dvKey, excludeIndex = -1) {
      return this.dvLines.some((line, i) => i !== excludeIndex && line.dv_key === dvKey);
    },

    clearJustAddedDvLineIndex() {
      this.justAddedDvLineIndex = null;
    },

    removeDvLine(index) {
      ListManager.remove(this.dvLines, index);
    },

    clearAllDvLines() {
      ListManager.clear(this.dvLines);
      this.justAddedDvLineIndex = null;
    },

    moveDvLine(fromIndex, toIndex) {
      ListManager.move(this.dvLines, fromIndex, toIndex);
    },

    // --- Standard Lines ---
    addLine() {
      this._addToList(this.linesWithoutDv, Factories.createLine(), "justAddedLineIndex");
    },

    clearJustAddedLineIndex() {
      this.justAddedLineIndex = null;
    },

    removeLine(index) {
      ListManager.remove(this.linesWithoutDv, index);
    },

    clearAllLines() {
      ListManager.clear(this.linesWithoutDv);
      this.justAddedLineIndex = null;
    },

    moveLine(fromIndex, toIndex) {
      ListManager.move(this.linesWithoutDv, fromIndex, toIndex);
    },

    // --- Blends ---
    addBlend() {
      this._addToList(this.blends, Factories.createBlend(), "justAddedBlendIndex");
    },

    removeBlend(index) {
      ListManager.remove(this.blends, index);
    },

    clearAllBlends() {
      ListManager.clear(this.blends);
      this.justAddedBlendIndex = null;
    },

    clearJustAddedBlendIndex() {
      this.justAddedBlendIndex = null;
    },

    moveBlend(fromIndex, toIndex) {
      ListManager.move(this.blends, fromIndex, toIndex);
    },

    // --- Dietary Lines ---
    updateDietaryLine(dvKey, amount) {
      const line = this.dietaryLines.find((l) => l.dv_key === dvKey);
      if (line) line.amount = amount;
    },

    clearAllDietaryLines() {
      this.dietaryLines.forEach((line) => {
        line.amount = "";
      });
    },

    // --- Constituents ---
    addConstituent(blendIndex) {
      const blend = this.blends[blendIndex];
      if (blend) {
        ListManager.add(blend.constituents, Factories.createConstituent(), () =>
          this._generateUid()
        );
      }
    },

    removeConstituent(blendIndex, constituentIndex) {
      const blend = this.blends[blendIndex];
      if (blend) {
        ListManager.remove(blend.constituents, constituentIndex);
      }
    },

    moveConstituent(blendIndex, fromIndex, toIndex) {
      const blend = this.blends[blendIndex];
      if (blend) {
        ListManager.move(blend.constituents, fromIndex, toIndex);
      }
    },

    // --- State Setters (Preserved for backward compatibility) ---
    getPayload() {
      return {
        supplement_label: Transformers.labelForServer(this),
      };
    },

    setCalculatedValues(values) {
      this.calculatedValues = values;
      this.isCalculating = false;
    },

    setValidationWarnings(warnings) {
      this.validationWarnings = warnings || {};
    },

    setCalculating(value) {
      this.isCalculating = value;
    },

    setSaving(value) {
      this.isSaving = value;
    },

    setSaved() {
      this.lastSavedAt = new Date();
      this.isSaving = false;
      this.error = null;
    },

    setError(error) {
      this.error = error;
      this.isSaving = false;
    },

    setFailedPayload(payload) {
      this.lastFailedPayload = payload;
      this.canRetry = true;
    },

    clearFailedPayload() {
      this.lastFailedPayload = null;
      this.canRetry = false;
    },

    getRetryPayload() {
      return this.lastFailedPayload;
    },

    registerRetryCallback(callback) {
      this.retryCallback = callback;
    },

    unregisterRetryCallback() {
      this.retryCallback = null;
    },

    retrySave() {
      if (this.retryCallback) {
        this.retryCallback();
      }
    },

    // --- View State ---
    setRenderedDimensions(text) {
      this.renderedDimensionsText = text;
    },

    toggleDisplayAgeCategory(value) {
      const idx = this.displayAgeCategories.indexOf(value);
      if (idx >= 0) {
        this.displayAgeCategories.splice(idx, 1);
      } else {
        this.displayAgeCategories.push(value);
      }
      this.showSpinner = true;
    },

    updateLockVersion(newVersion) {
      if (!Number.isFinite(newVersion)) return;
      const wasConflicted = this.isConflicted;
      this.lockVersion = Math.max(this.lockVersion, newVersion);
      this.serverLockVersion = Math.max(this.serverLockVersion, newVersion);
      // A concurrent write (e.g., logo save) may have caused a stale 409
      // on autosave. If the version we just received brings us up to date,
      // clear the conflict and retry.
      if (wasConflicted && this.lockVersion >= this.serverLockVersion) {
        this.isConflicted = false;
        this.error = null;
        if (this.retryCallback) {
          this.retryCallback();
        }
      }
    },

    setServerLockVersion(version) {
      if (!Number.isFinite(version)) return;
      this.serverLockVersion = Math.max(this.serverLockVersion, version);
    },

    setConflicted(value) {
      this.isConflicted = value;
    },

    async reloadFromServer() {
      this.debugLog("[SupplementLabelStore] reloadFromServer");
      const now = Date.now();
      if (this.isReloading || (this.lastReloadAt && now - this.lastReloadAt < 2000)) {
        return false;
      }

      this.isReloading = true;
      try {
        const data = await SupplementApi.reloadLabel(this.id);
        this.initialize(data);
        this.lastReloadAt = Date.now();
        window.Flash?.success("Label reloaded with latest changes");
        return true;
      } catch (e) {
        window.Flash?.error(e.message || "Failed to reload label. Please refresh the page.");
        return false;
      } finally {
        this.isReloading = false;
      }
    },
  },
});
